import noop from 'lodash/noop'
import React, { FC, PropsWithChildren, useContext, useState } from 'react'
import { toast } from 'react-toastify'

import { Piece } from 'features/board/board'
import {
  isEmptyHex,
  isHexValidForDeploy,
  isHexValidForDistribute,
  isKingPiece,
  isMyPiece,
  isWallPiece,
} from 'features/board/utils'
import { useGameEvents } from 'features/firebase/use-game-events'
import { HexId } from 'features/hex-board/hex-board'
import { isNeighbor } from 'features/hex-board/utils'
import { useSoundContext } from 'features/sound/sound-context'

import { useBoolean } from 'shared/hooks/use-boolean'
import { getKeys } from 'shared/utils'

import { useGameContext } from './game-context'
import { useDrawAction } from './use-draw-action'
import { isGameOpen, switchRestrictions } from './utils'

interface GameEventProvider extends ReturnType<typeof useDrawAction> {
  hexSelected: HexId | null
  handPieceSelectedIdx: number | null
  pieceSelected: Piece | null
  crownNextPiece: boolean
  isCrownedPieceOnBoard: boolean
  isTurnProtection: boolean
  handleHexClick: (hexPosition: HexId) => void
  handleHandPieceIdxClick: (pieceIdx: number) => void
  handleHandClick: () => void
  handleFightClick: AnyFunction
  handleCrownNextPiece: AnyFunction
  toggleTurnProtection: AnyFunction
}

export const GameEventContext = React.createContext<GameEventProvider>({
  hexSelected: null,
  handPieceSelectedIdx: null,
  crownNextPiece: false,
  pieceSelected: null,
  isCrownedPieceOnBoard: false,
  isTurnProtection: true,
  playerRequestingDraw: false,
  rejectDraw: async () => {},
  requestDraw: async () => {},
  handleHexClick: noop,
  handleHandPieceIdxClick: noop,
  handleHandClick: noop,
  handleFightClick: noop,
  handleCrownNextPiece: noop,
  toggleTurnProtection: noop,
  acceptDraw: noop,
})

export const GameEventProvider: FC<PropsWithChildren> = ({ children }) => {
  const [hexSelected, setHexSelected] = useState<HexId | null>(null)
  const [handPieceSelectedIdx, setHandPieceSelectedIdx] = useState<number | null>(null)
  const [crownNextPiece, toggleCrownNextPiece] = useBoolean()
  const [isTurnProtection, toggleTurnProtection] = useBoolean(true)
  const { playClickSound } = useSoundContext()

  const {
    dispatchDistributeEvent,
    dispatchSwitchEvent,
    dispatchWithdrawEvent,
    dispatchDeployEvent,
    dispatchMoveEvent,
    dispatchFightEvent,
  } = useGameEvents()

  const drawAction = useDrawAction()

  const {
    game,
    phase,
    hexBoard,
    relativeGameData: { hand, playerColor, turn },
  } = useGameContext()

  const pieceSelected = hexSelected ? (hexBoard[hexSelected] as Piece) : null
  const isCrownedPieceOnBoard = getKeys(hexBoard).some(hexId => {
    const piece = hexBoard[hexId]

    return piece && !isWallPiece(piece) && isMyPiece(playerColor.bottom, piece) && isKingPiece(piece)
  })

  const protection = (cb: AnyFunction) => finishedProtection(turnProtection(cb))

  const finishedProtection = (cb: AnyFunction) => {
    if (!isGameOpen(game.status)) return noop
    return cb
  }

  const turnProtection = (cb: AnyFunction) => {
    if (isTurnProtection && !turn.bottom) return noop
    return cb
  }

  const handleFightClick = protection(() => {
    dispatchFightEvent()
    setHandPieceSelectedIdx(null)
    setHexSelected(null)
  })

  const handleHandClick = protection(() => {
    if (!hexSelected || !pieceSelected) return

    if (isMyPiece(playerColor.bottom, pieceSelected) && !isKingPiece(pieceSelected)) {
      dispatchWithdrawEvent({ player: playerColor.bottom, hexId: hexSelected })
      setHexSelected(null)
    }
  })

  const handleHandPieceIdxClick = protection((pieceIdx: number) => {
    setHexSelected(null)
    if (pieceIdx === handPieceSelectedIdx) {
      setHandPieceSelectedIdx(null)
    } else {
      setHandPieceSelectedIdx(pieceIdx)
    }
  })

  const handleHexClick = protection(async (hexId: HexId) => {
    if (phase === 'distribute') {
      await handleDistributePhaseHexClick(hexId)
    } else if (phase === 'fight') {
      await handleFightPhaseHexClick(hexId)
    }
  })

  const handleDistributePhaseHexClick = async (hexId: HexId) => {
    if (handPieceSelectedIdx !== null) {
      const piece = hand.bottom[handPieceSelectedIdx]
      if (isHexValidForDistribute(playerColor.bottom, hexId) && isEmptyHex(hexBoard[hexId])) {
        if (crownNextPiece || (!isCrownedPieceOnBoard && hand.bottom.length === 2)) {
          await dispatchDistributeEvent({ hexId, piece: `k${piece}` })
          toggleCrownNextPiece()
        } else {
          await dispatchDistributeEvent({ hexId, piece })
        }
        setHandPieceSelectedIdx(null)
      }
    }
  }

  const handleFightPhaseHexClick = async (hexId: HexId) => {
    if (isEmptyHex(hexBoard[hexId])) {
      // deployment
      if (handPieceSelectedIdx !== null) {
        if (isHexValidForDeploy(playerColor.bottom, hexId, hexBoard)) {
          const piece = hand.bottom[handPieceSelectedIdx]
          await dispatchDeployEvent({ hexId, piece })
          setHandPieceSelectedIdx(null)
        }
      }
      // movement
      else if (hexSelected && isNeighbor(hexSelected, hexId) && isMyPiece(playerColor.bottom, pieceSelected)) {
        await dispatchMoveEvent({ from: hexSelected, to: hexId })
        setHexSelected(null)
      }
    } else if (isMyPiece(playerColor.bottom, hexBoard[hexId])) {
      // switch
      if (hexSelected && isNeighbor(hexSelected, hexId) && isMyPiece(playerColor.bottom, pieceSelected)) {
        if (switchRestrictions(game.events, [hexSelected, hexId])) {
          toast.warn("Can't switch the same pawns again")
        } else {
          await dispatchSwitchEvent({ from: hexSelected, to: hexId })
          setHexSelected(null)
        }
      }
      // select / unselect
      else if (hexSelected === hexId) {
        playClickSound()
        setHexSelected(null)
      } else {
        playClickSound()
        setHexSelected(hexId)
        setHandPieceSelectedIdx(null)
      }
    }
  }

  const handleCrownNextPiece = () => {
    playClickSound()
    !isCrownedPieceOnBoard && toggleCrownNextPiece()
  }

  return (
    <GameEventContext.Provider
      value={{
        hexSelected,
        pieceSelected,
        handPieceSelectedIdx,
        crownNextPiece,
        isCrownedPieceOnBoard,
        isTurnProtection,
        ...drawAction,
        toggleTurnProtection,
        handleHandPieceIdxClick,
        handleHexClick,
        handleHandClick,
        handleFightClick,
        handleCrownNextPiece,
      }}
    >
      {children}
    </GameEventContext.Provider>
  )
}

export const useGameEventContext = () => useContext(GameEventContext)
